import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, Eye, EyeOff, Stethoscope, UserPlus } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from '../config/api';

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseIssuer, setLicenseIssuer] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [referrerName, setReferrerName] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';

  useEffect(() => {
    if (refCode) {
      axios.get(`${BACKEND_URL}/api/referral/validate/${refCode}`)
        .then(({ data }) => setReferrerName(data.referrer_name))
        .catch(() => {});
    }
  }, [refCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    const result = await register(email, password, name, licenseNumber, licenseIssuer, refCode || undefined);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F8F7F4]">
        <div className="w-full max-w-md animate-fade-in">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#4B6651] rounded-lg flex items-center justify-center">
              <Stethoscope className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-[#2A2C2A] font-['Work_Sans']">VetNeuroHelp</h1>
              <p className="text-sm text-[#5C5F5C]">Neurological Lesion Localization</p>
            </div>
          </div>

          <Card className="border-[#E5E4E0] shadow-none">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-['Work_Sans']">Create an account</CardTitle>
              <CardDescription>Start using AI-powered neurological diagnosis</CardDescription>
            </CardHeader>
            <CardContent>
              {referrerName && (
                <div className="mb-4 flex items-center gap-2 p-3 rounded-lg bg-[#4B6651]/10 text-[#4B6651]" data-testid="referral-banner">
                  <UserPlus className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm">Referred by <strong>{referrerName}</strong></span>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-[#D94F4F]/10 text-[#D94F4F]" data-testid="register-error">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">{error}</span>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[#2A2C2A]">Full Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Dr. Jane Smith"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="bg-[#F8F7F4] border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]"
                    data-testid="register-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#2A2C2A]">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-[#F8F7F4] border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]"
                    data-testid="register-email"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="licenseNumber" className="text-[#2A2C2A]">License Number</Label>
                    <Input
                      id="licenseNumber"
                      type="text"
                      placeholder="e.g., DVM-123456"
                      value={licenseNumber}
                      onChange={(e) => setLicenseNumber(e.target.value)}
                      required
                      className="bg-[#F8F7F4] border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]"
                      data-testid="register-license-number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="licenseIssuer" className="text-[#2A2C2A]">Issuing Entity</Label>
                    <Input
                      id="licenseIssuer"
                      type="text"
                      placeholder="e.g., State Vet Board"
                      value={licenseIssuer}
                      onChange={(e) => setLicenseIssuer(e.target.value)}
                      required
                      className="bg-[#F8F7F4] border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]"
                      data-testid="register-license-issuer"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#2A2C2A]">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="bg-[#F8F7F4] border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651] pr-10"
                      data-testid="register-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C5F5C] hover:text-[#2A2C2A]"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-[#2A2C2A]">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="bg-[#F8F7F4] border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]"
                    data-testid="register-confirm-password"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#4B6651] hover:bg-[#3E5543] text-white"
                  disabled={loading}
                  data-testid="register-submit"
                >
                  {loading ? 'Creating account...' : 'Create account'}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm">
                <span className="text-[#5C5F5C]">Already have an account? </span>
                <Link to="/login" className="text-[#4B6651] hover:text-[#3E5543] font-medium" data-testid="login-link">
                  Sign in
                </Link>
              </div>

              <div className="mt-4 flex justify-center gap-3 text-xs text-[#5C5F5C]">
                <Link to="/terms" className="hover:text-[#4B6651] hover:underline">Terms</Link>
                <span>|</span>
                <Link to="/privacy" className="hover:text-[#4B6651] hover:underline">Privacy</Link>
                <span>|</span>
                <Link to="/contact" className="hover:text-[#4B6651] hover:underline">Contact</Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:flex-1 relative">
        <img
          src="https://images.pexels.com/photos/6816851/pexels-photo-6816851.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Veterinarian with cat"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute bottom-8 left-8 right-8 text-white">
          <h2 className="text-3xl font-semibold font-['Work_Sans'] mb-2">
            Evidence-Based Diagnostics
          </h2>
          <p className="text-white/80">
            Powered by veterinary neurology research from leading .edu institutions
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
